<template>
  <div>
    <div
      v-if="show"
      class="fixed left-0 top-0 z-[10001] box-border grid h-full w-full items-center justify-center overflow-auto bg-black/[.65] pt-1.5"
    >
      <div
        data-testid="block-language-selector"
        class="relative m-auto w-[380px] rounded-xl bg-gray-700 p-5"
      >
        <div class="mb-7 font-inter text-base font-bold leading-4 text-gray-50">
          {{ title }}
        </div>
        <div
          class="flex grow items-center font-inter text-sm font-medium leading-[1.5] text-gray-100"
        >
          <p v-html="sanitizedText"></p>
        </div>
        <div class="mt-6 flex items-center gap-2">
          <TLabel>{{
            $t("layout.blockLanguageSelector.selectLanguage")
          }}</TLabel>
          <TDropdown
            data-testid="block-language-selector-dropdown"
            :selected="selectedLang"
            :data="langs"
            @selection-changed="onSelectionChanged"
          />
        </div>
        <div class="mt-5 flex justify-end gap-2">
          <t-button data-testid="cancel" @click="onCancel">
            {{ $t("actions.cancel") }}
          </t-button>
          <t-button data-testid="confirm" success @click="onOkButtonClick">
            {{ $t("layout.blockLanguageSelector.select") }}
          </t-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { TButton, TDropdown, TLabel } from "@topol/twig-ui";
  import type { TDropdownOption } from "@topol/twig-ui";

  import { ref, computed, watch, onBeforeUnmount } from "vue";
  import { sanitizeBold } from "@/utils/sanitizeBold";

  type DropdownData = {
    label?: string;
    value: string;
  };

  const show = ref(false);
  const langs = ref<DropdownData[]>();
  const resolve = ref<any>();
  const selectedLang = ref<string | undefined>();
  const text = ref<string>("");
  const title = ref<string>("");

  const sanitizedText = computed(() => sanitizeBold(text.value));

  // const props = defineProps<{
  //   langs: string[];
  // }>();

  const open = async (props: {
    langs: string[];
    description: string;
    title: string;
  }) => {
    return new Promise((res) => {
      resolve.value = res;
      const langsData: DropdownData[] = [];
      for (const lang of props.langs) {
        langsData.push({
          label: lang,
          value: lang,
        });
      }
      langs.value = langsData;
      text.value = props.description;
      title.value = props.title;
      selectedLang.value = langs.value[0].value;
      show.value = true;
    });
  };

  const onSelectionChanged = (option: TDropdownOption) => {
    selectedLang.value = option.value;
  };

  const onOkButtonClick = () => {
    if (selectedLang.value) {
      show.value = false;
      resolve.value && resolve.value(selectedLang.value);
    }
  };

  const onCancel = () => {
    show.value = false;
    resolve.value && resolve.value(null);
  };

  const onKeydown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      onCancel();
    }
  };

  watch(show, (isShown) => {
    if (isShown) {
      window.addEventListener("keydown", onKeydown);
    } else {
      window.removeEventListener("keydown", onKeydown);
    }
  });

  onBeforeUnmount(() => {
    window.removeEventListener("keydown", onKeydown);
  });

  defineExpose({
    open,
  });
</script>
